/**
 * Purchase Cards — Package module behaviour.
 *
 * Two subsystems:
 *   4a  Package add-to-cart. Mirrors the theme's native path in
 *       product-form.js:162-279 by importing the same helpers, rather than
 *       hand-rolling a fetch the way blocks/product-addons.liquid:634-656 does.
 *   4b  The "More Details" modal, ported from blocks/product-addons.liquid:811-986.
 *
 * Package cards and accessory cards share ONE add implementation. Both are a
 * direct add-to-cart; neither carries a selection state. This file writes
 * nothing into the price container and never touches the main product's form —
 * no listener on it, no injected input, no preventDefault.
 *
 * This file NEVER touches the main product's Add to Cart. Every card is an
 * independent add; there is no preventDefault on any other form.
 *
 * All listeners are delegated from `document` and matched on data-attributes
 * this lane owns, so nothing breaks when a section re-renders (C-5: the legacy
 * script's static querySelectorAll binding at DOMContentLoaded is the failure
 * mode being avoided here).
 */

import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const ADDED_ANIMATION_DURATION = 2000; // matches ADD_TO_CART_TEXT_ANIMATION_DURATION
const GENERIC_ERROR = 'Could not add this to your cart. Please try again.';

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const moduleEl = () => document.querySelector('.package-module[data-money-format]');

/** Read a cookie by name. Mirrors addonsGetCookie (product-addons.liquid:319). */
function readCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : '';
}

/**
 * Shopify's money formatter. The theme does not define Shopify.formatMoney,
 * which the legacy modal calls at product-addons.liquid:951 — that swatch
 * handler throws today. The popup's swatches carry raw cents
 * (accessories-popup.liquid:72-76), so a formatter is genuinely needed.
 * @param {number|string} cents
 * @param {string} format - shop.money_format
 */
function formatMoney(cents, format) {
  const value = typeof cents === 'string' ? Number(cents.replace(/[^0-9.-]/g, '')) : Number(cents);
  if (!Number.isFinite(value)) return '';

  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format?.match(placeholder);
  if (!match) return (value / 100).toFixed(2);

  const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
    const fixed = (number / 100.0).toFixed(precision);
    const [whole, part] = fixed.split('.');
    const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return part ? grouped + decimal + part : grouped;
  };

  let body;
  switch (match[1]) {
    case 'amount_no_decimals':
      body = withDelimiters(value, 0);
      break;
    case 'amount_with_comma_separator':
      body = withDelimiters(value, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      body = withDelimiters(value, 0, '.', ',');
      break;
    default:
      body = withDelimiters(value, 2);
  }
  return format.replace(placeholder, body);
}

/* ------------------------------------------------------------------ *
 * 4a — Package add to cart
 * ------------------------------------------------------------------ */

/**
 * ONE add contract, shared by package cards and accessory cards. A card
 * declares [data-purchase-add] (the button) and [data-purchase-error] (the
 * message surface); optionally [data-purchase-qty] (absent means 1); and a
 * variant source — a checked selector radio, a [data-purchase-variant] hidden
 * input, or data-variant-id on the card itself.
 * @param {HTMLElement} card
 */
function getQuantity(card) {
  const input = card.querySelector('[data-purchase-qty]');
  if (!input) return 1;
  const value = Math.floor(Number(input.value));
  return Number.isFinite(value) && value >= 1 ? value : 1;
}

/** @param {HTMLElement} card */
function getVariantId(card) {
  const checked = card.querySelector('.package-card__selector-input:checked');
  if (checked) return checked.value;
  return card.querySelector('[data-purchase-variant]')?.value ?? card.dataset.variantId ?? '';
}

/** @param {HTMLElement} card @param {string} message */
function showError(card, message) {
  const el = card.querySelector('[data-purchase-error]');
  if (!el) return;
  el.textContent = message;
  el.hidden = false;
}

/** @param {HTMLElement} card */
function clearError(card) {
  const el = card.querySelector('[data-purchase-error]');
  if (!el) return;
  el.textContent = '';
  el.hidden = true;
}

/** @param {HTMLElement} card */
async function addCardToCart(card) {
  const button = card.querySelector('[data-purchase-add]');
  if (!button || button.disabled) return;

  const variantId = getVariantId(card);
  if (!variantId) {
    showError(card, GENERIC_ERROR);
    return;
  }

  const quantity = getQuantity(card);

  clearError(card);
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', String(quantity));

  // Section ids for the drawer morph (component-cart-items.js:335). Appended
  // ONCE — product-form.js:185 appends inside its forEach, one entry per
  // cart-items-component.
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const config = fetchConfig('javascript', { body: formData });

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: { ...config.headers, Accept: 'text/html' },
    });
    const data = await response.json();

    if (data.status) {
      // Server-side rejection: sold out, quantity rule, etc.
      document.dispatchEvent(
        new CartErrorEvent('purchase-cards', data.message, data.description, data.errors)
      );
      showError(card, data.description || data.message || GENERIC_ERROR);
      return;
    }

    // source is 'product-form-component' deliberately: cart-icon.js:45 treats
    // that value as "increment the badge" and anything else as "set the badge
    // to itemCount", which would show the wrong total.
    document.dispatchEvent(
      new CartAddEvent({}, String(variantId), {
        source: 'product-form-component',
        itemCount: quantity,
        productId: card.dataset.purchaseProductId,
        sections: data.sections,
      })
    );

    button.classList.add('atc-added');
    setTimeout(() => button.classList.remove('atc-added'), ADDED_ANIMATION_DURATION);
  } catch (error) {
    // D-26: never swallow. product-form.js:272 logs and leaves a success-looking
    // UI; this surfaces instead.
    console.error('[purchase-cards] add to cart failed', error);
    showError(card, GENERIC_ERROR);
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

/* ------------------------------------------------------------------ *
 * Stepper + selectors
 * ------------------------------------------------------------------ */

/** @param {HTMLElement} card @param {number} delta */
function stepQuantity(card, delta) {
  const input = card.querySelector('[data-purchase-qty]');
  if (!(input instanceof HTMLInputElement)) return;
  const next = Math.max(1, getQuantity(card) + delta);
  input.value = String(next);
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Keep the card's own price display in step with the chosen variant. Ported
 * from the legacy swatch handler (product-addons.liquid:934-962); the strings
 * are rendered server-side into data attributes so no money formatting is
 * needed here.
 * @param {HTMLElement} card
 * @param {HTMLInputElement} input
 */
function syncPrice(card, input) {
  const price = card.querySelector('[data-package-price]');
  const compare = card.querySelector('[data-package-compare]');
  const discount = card.querySelector('[data-package-discount]');

  if (price) price.textContent = input.dataset.price ?? price.textContent;

  if (compare) {
    const value = input.dataset.compare ?? '';
    compare.textContent = value;
    compare.hidden = value === '';
  }
  if (discount) {
    const value = input.dataset.discount ?? '';
    discount.textContent = value;
    discount.hidden = value === '';
  }
}

/* ------------------------------------------------------------------ *
 * 4b — "More Details" modal (ported)
 * ------------------------------------------------------------------ */

let popupRefs = null;

function ensurePopup() {
  if (popupRefs) return popupRefs;

  document.body.insertAdjacentHTML(
    'beforeend',
    `<div class="ro-popup-container" id="ro-popup-container">
       <div id="ro-details-popup" class="ro-details-popup ro-details-popup--padding">
         <div id="ro-popup-option-products"></div>
       </div>
     </div>`
  );

  const container = document.getElementById('ro-popup-container');
  const popup = document.getElementById('ro-details-popup');
  const content = document.getElementById('ro-popup-option-products');

  const close = () => {
    container.classList.remove('_active');
    popup.classList.remove('popup-opened');
    content.innerHTML = '';
  };

  // Backdrop click
  container.addEventListener('click', (event) => {
    if (event.target === container) close();
  });
  // Close button — delegated, since the button arrives with the fetched markup
  container.addEventListener('click', (event) => {
    if (event.target.closest('.ro-popup__close-btn')) close();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && container.classList.contains('_active')) close();
  });

  popupRefs = { container, popup, content, close };
  return popupRefs;
}

const slideDown = (element, duration = 300) => {
  element.style.removeProperty('display');
  let display = window.getComputedStyle(element).display;
  if (display === 'none') display = 'block';
  element.style.display = display;
  const height = element.offsetHeight;
  element.style.overflow = 'hidden';
  element.style.height = 0;
  element.style.paddingTop = 0;
  element.style.paddingBottom = 0;
  element.style.marginTop = 0;
  element.style.marginBottom = 0;
  element.offsetHeight;
  element.style.transitionProperty = 'height, margin, padding';
  element.style.transitionDuration = duration + 'ms';
  element.style.height = height + 'px';
  element.style.removeProperty('padding-top');
  element.style.removeProperty('padding-bottom');
  element.style.removeProperty('margin-top');
  element.style.removeProperty('margin-bottom');
  window.setTimeout(() => {
    element.style.removeProperty('height');
    element.style.removeProperty('overflow');
    element.style.removeProperty('transition-duration');
    element.style.removeProperty('transition-property');
  }, duration);
};

const slideUp = (element, duration = 300) => {
  element.style.transitionProperty = 'height, margin, padding';
  element.style.transitionDuration = duration + 'ms';
  element.style.height = element.offsetHeight + 'px';
  element.offsetHeight;
  element.style.overflow = 'hidden';
  element.style.height = 0;
  element.style.paddingTop = 0;
  element.style.paddingBottom = 0;
  element.style.marginTop = 0;
  element.style.marginBottom = 0;
  window.setTimeout(() => {
    element.style.display = 'none';
    element.style.removeProperty('height');
    element.style.removeProperty('padding-top');
    element.style.removeProperty('padding-bottom');
    element.style.removeProperty('margin-top');
    element.style.removeProperty('margin-bottom');
    element.style.removeProperty('overflow');
    element.style.removeProperty('transition-duration');
    element.style.removeProperty('transition-property');
  }, duration);
};

/** @param {HTMLElement} scope */
function initPopupGallery(scope) {
  // Guarded: the legacy call at product-addons.liquid:837 is bare and throws
  // if the bundle is missing, leaving the trigger stuck on "Loading...".
  if (typeof window.Swiper !== 'function') {
    console.warn('[purchase-cards] Swiper unavailable; package gallery left static');
    return;
  }
  if (!scope.querySelector('.gallery_product_slider')) return;

  new window.Swiper('.gallery_product_slider', {
    loop: false,
    slidesPerView: 1,
    spaceBetween: 0,
    navigation: {
      nextEl: '.product-gallery__button-next',
      prevEl: '.product-gallery__button-prev',
    },
    pagination: { el: '.swiper-pagination', clickable: true },
    resistanceRatio: 0.000001,
    mousewheel: {
      eventsTarget: '.gallery_product_slider',
      forceToAxis: true,
      sensitivity: 0.5,
    },
  });
}

/** @param {HTMLElement} scope */
function initPopupTabs(scope) {
  scope.querySelectorAll('.popup-product__block-content .c-overview__tab-name').forEach((tabName) => {
    tabName.addEventListener('click', function () {
      const parent = this.closest('.c-overview__tab-block');
      if (!parent || !this.nextElementSibling) return;
      parent.classList.toggle('is-active');
      if (parent.classList.contains('is-active')) {
        slideDown(this.nextElementSibling);
      } else {
        slideUp(this.nextElementSibling);
      }
    });
  });
}

/** @param {HTMLElement} scope */
function initPopupSwatches(scope) {
  const format = moduleEl()?.dataset.moneyFormat ?? '';
  const swatches = scope.querySelectorAll('.popup-product__block-content .js-ro-swatch');

  swatches.forEach((swatch) => {
    swatch.addEventListener('click', function (event) {
      event.preventDefault();
      swatches.forEach((s) => s.classList.remove('_active'));
      this.classList.add('_active');

      const { variantId, price, comparePrice, salePrice } = this.dataset;
      const parent = this.closest('.popup-product__block-info');
      if (!parent) return;

      const priceEl = parent.querySelector('.current_price .money');
      if (priceEl) priceEl.textContent = formatMoney(price, format);

      if (salePrice) {
        const wasPrice = parent.querySelector('.was-price .money');
        if (wasPrice) wasPrice.textContent = formatMoney(comparePrice, format);
        const saleEl = parent.querySelector('.pr-list__sale');
        if (saleEl) saleEl.textContent = `${formatMoney(salePrice, format)} OFF`;
      }

      scope
        .querySelectorAll('.popup-product__block-content [data-specs-variant-id]')
        .forEach((el) => {
          el.style.display = 'none';
        });
      const variantSpec = scope.querySelector(
        `.popup-product__block-content [data-specs-variant-id="${variantId}"]`
      );
      if (variantSpec) variantSpec.style.display = '';
    });
  });
}

/** @param {HTMLElement} scope */
function revealGeoContent(scope) {
  const settings = moduleEl()?.dataset ?? {};
  const geoipOff = settings.geoipOff === 'true';
  const geoipRegion = settings.geoipRegion ?? '';
  const countryCode = readCookie('country_code');

  if (countryCode === 'US' || geoipOff || (geoipRegion === 'CAN' && countryCode === 'CA')) {
    scope.querySelectorAll('.US').forEach((el) => {
      el.style.display = 'flex';
    });
  }
}

/** @param {HTMLElement} trigger */
async function openDetails(trigger) {
  const handle = trigger.dataset.handle;
  if (!handle) return;

  const card = trigger.closest('[data-purchase-card]');
  const label = trigger.dataset.label || trigger.textContent.trim();
  const { container, popup, content } = ensurePopup();

  trigger.textContent = 'Loading...';
  trigger.classList.add('is-loading');
  trigger.disabled = true;

  try {
    const response = await fetch(`/products/${handle}?view=accessories-popup`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const markup = await response.text();
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const productPopup = parsed.querySelector('[data-products-popup-container]');
    if (!productPopup) throw new Error('popup container missing from response');

    content.innerHTML = '';
    content.insertAdjacentElement('afterbegin', productPopup);

    initPopupGallery(popup);
    revealGeoContent(popup);
    initPopupTabs(popup);
    initPopupSwatches(popup);

    container.classList.add('_active');
    popup.classList.add('popup-opened');
  } catch (error) {
    console.error('[purchase-cards] details modal failed', error);
    if (card) showError(card, 'Could not load the product details. Please try again.');
  } finally {
    trigger.textContent = label;
    trigger.classList.remove('is-loading');
    trigger.disabled = false;
  }
}

/* ------------------------------------------------------------------ *
 * Delegation
 * ------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const step = target.closest('[data-package-step]');
  if (step) {
    const card = step.closest('[data-purchase-card]');
    if (card) stepQuantity(card, Number(step.dataset.packageStep));
    return;
  }

  // Both card types. Nothing else on either card is clickable: an accessory add
  // is now a direct add-to-cart, so a mis-tap on the card body must not put a
  // charge in the cart.
  const add = target.closest('[data-purchase-add]');
  if (add) {
    const card = add.closest('[data-purchase-card]');
    if (card) addCardToCart(card);
    return;
  }

  const details = target.closest('[data-card-details]');
  if (details) {
    event.preventDefault();
    openDetails(details);
  }
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;

  if (target.classList.contains('package-card__selector-input')) {
    const card = target.closest('[data-purchase-card]');
    if (card) syncPrice(card, target);
    return;
  }

  if (target.matches('[data-purchase-qty]')) {
    const value = Math.floor(Number(target.value));
    target.value = String(Number.isFinite(value) && value >= 1 ? value : 1);
  }
});
